import { useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Table2, ChartColumn } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import {
  TIER_ORDER, TIER_META, tierColor,
  formatCurrency, formatCompact, monthShort, monthLong,
} from "./format";

const HORIZONS = [6, 12, 18, 24];

function Segmented({ value, options, onChange }) {
  return (
    <div className="inline-flex items-center rounded-full border border-border bg-warm-100/60 p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 rounded-full text-xs transition-all duration-250 ease-brand ${
            value === opt.value
              ? "bg-card text-foreground border border-border"
              : "text-muted-foreground hover:text-foreground border border-transparent"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ForecastTooltip({ active, payload, label, cumulative }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const total = cumulative ? row.cumulative : row.total;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
      <p className="micro-label mb-1.5">{monthLong(row.month)}</p>
      {TIER_ORDER.map((tier) => {
        const v = payload.find((p) => p.dataKey === tier)?.value || 0;
        if (!v) return null;
        return (
          <div key={tier} className="flex items-center justify-between gap-6 text-xs py-0.5">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span
                className="inline-block w-2.5 h-2.5 rounded-[3px]"
                style={{ background: payload.find((p) => p.dataKey === tier)?.fill || payload.find((p) => p.dataKey === tier)?.color }}
              />
              {TIER_META[tier].label}
            </span>
            <span className="tabular text-foreground">{formatCurrency(v)}</span>
          </div>
        );
      })}
      <div className="flex items-center justify-between gap-6 text-xs pt-1 mt-1 border-t border-border">
        <span className="text-muted-foreground">{cumulative ? "Cumulative" : "Total"}</span>
        <span className="tabular text-foreground">{formatCurrency(total)}</span>
      </div>
    </div>
  );
}

export default function ForecastPanel({ forecast, unscheduled, kpis }) {
  const { theme } = useTheme();
  const [horizon, setHorizon] = useState(12);
  const [view, setView] = useState("monthly"); // monthly | cumulative
  const [showTable, setShowTable] = useState(false);

  const data = useMemo(() => {
    const sliced = (forecast || []).slice(0, horizon);
    if (view === "monthly") return sliced;
    // Cumulative per tier, so the stacked area keeps the certainty split.
    const running = { active: 0, ready: 0, signed: 0, forecasted: 0 };
    return sliced.map((m) => {
      TIER_ORDER.forEach((t) => { running[t] += m[t] || 0; });
      return { ...m, ...running, total: m.total };
    });
  }, [forecast, horizon, view]);

  const horizonTotal = useMemo(
    () => (forecast || []).slice(0, horizon).reduce((s, m) => s + (m.total || 0), 0),
    [forecast, horizon]
  );
  const signedHorizonTotal = useMemo(
    () => (forecast || []).slice(0, horizon)
      .reduce((s, m) => s + (m.active || 0) + (m.ready || 0) + (m.signed || 0), 0),
    [forecast, horizon]
  );

  const gridStroke = theme === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
  const axisColor = theme === "dark" ? "#8A8783" : "#8A8783";
  const surface = theme === "dark" ? "#121212" : "#FFFFFF";

  return (
    <div className="space-y-4">
      {/* Controls + headline, one row */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="micro-label mb-1">
            Next {horizon} months · expected recurring revenue
          </p>
          <div className="flex items-baseline gap-3">
            <span className="hero-numeral text-4xl">{formatCompact(horizonTotal)}</span>
            <span className="text-sm text-muted-foreground">
              {formatCompact(signedHorizonTotal)} of it signed
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            value={horizon}
            onChange={setHorizon}
            options={HORIZONS.map((h) => ({ value: h, label: `${h} mo` }))}
          />
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: "monthly", label: "Monthly" },
              { value: "cumulative", label: "Cumulative" },
            ]}
          />
          <button
            onClick={() => setShowTable((v) => !v)}
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
            title={showTable ? "Show chart" : "Show table"}
          >
            {showTable ? <ChartColumn className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Table2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
            {showTable ? "Chart" : "Table"}
          </button>
        </div>
      </div>

      {/* Legend — identity never rides on color alone */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {TIER_ORDER.map((tier) => (
          <span key={tier} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className="inline-block w-2.5 h-2.5 rounded-[3px] border border-border"
              style={{ background: tierColor(tier, theme) }}
            />
            {TIER_META[tier].label}
            <span className="opacity-60">· {TIER_META[tier].long}</span>
          </span>
        ))}
      </div>

      {!showTable ? (
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            {view === "monthly" ? (
              <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="28%">
                <CartesianGrid vertical={false} stroke={gridStroke} />
                <XAxis
                  dataKey="month" tickFormatter={monthShort} tickLine={false}
                  axisLine={{ stroke: gridStroke }}
                  tick={{ fontSize: 11, fill: axisColor }}
                  interval={horizon > 12 ? 1 : 0}
                />
                <YAxis
                  tickFormatter={formatCompact} tickLine={false} axisLine={false}
                  tick={{ fontSize: 11, fill: axisColor }} width={52}
                />
                <Tooltip content={<ForecastTooltip />} cursor={{ fill: gridStroke }} />
                {TIER_ORDER.map((tier) => (
                  <Bar
                    key={tier} dataKey={tier} stackId="mrr"
                    fill={tierColor(tier, theme)}
                    stroke={surface} strokeWidth={1}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            ) : (
              <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={gridStroke} />
                <XAxis
                  dataKey="month" tickFormatter={monthShort} tickLine={false}
                  axisLine={{ stroke: gridStroke }}
                  tick={{ fontSize: 11, fill: axisColor }}
                  interval={horizon > 12 ? 1 : 0}
                />
                <YAxis
                  tickFormatter={formatCompact} tickLine={false} axisLine={false}
                  tick={{ fontSize: 11, fill: axisColor }} width={52}
                />
                <Tooltip content={<ForecastTooltip cumulative />} cursor={{ stroke: gridStroke }} />
                {TIER_ORDER.map((tier) => (
                  <Area
                    key={tier} dataKey={tier} stackId="mrr" type="monotone"
                    fill={tierColor(tier, theme)} fillOpacity={0.85}
                    stroke={surface} strokeWidth={1}
                    isAnimationActive={false}
                  />
                ))}
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2.5 whitespace-nowrap"><span className="micro-label">Month</span></th>
                {TIER_ORDER.map((t) => (
                  <th key={t} className="text-right px-4 py-2.5 whitespace-nowrap"><span className="micro-label">{TIER_META[t].label}</span></th>
                ))}
                <th className="text-right px-4 py-2.5 whitespace-nowrap"><span className="micro-label">Total</span></th>
                <th className="text-right px-4 py-2.5 whitespace-nowrap"><span className="micro-label">Cumulative</span></th>
              </tr>
            </thead>
            <tbody>
              {(forecast || []).slice(0, horizon).map((m) => (
                <tr key={m.month} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-muted-foreground">{monthLong(m.month)}</td>
                  {TIER_ORDER.map((t) => (
                    <td key={t} className="px-4 py-2 text-right tabular">
                      {m[t] ? formatCurrency(m[t]) : <span className="opacity-30">—</span>}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right tabular">{formatCurrency(m.total)}</td>
                  <td className="px-4 py-2 text-right tabular">{formatCurrency(m.cumulative)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unscheduled?.count > 0 && (
        <p className="text-xs text-muted-foreground border-t border-border pt-3">
          Not on this curve: {unscheduled.count} subscription{unscheduled.count === 1 ? "" : "s"} worth{" "}
          <span className="tabular text-foreground">{formatCurrency(unscheduled.mrr)}/mo</span>{" "}
          with no start month yet — set one on the Subscriptions tab to bring
          {unscheduled.count === 1 ? " it" : " them"} into the forecast.
        </p>
      )}
    </div>
  );
}
